import {
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Typography,
  Box,
} from "@mui/material";
import { useMemo, useState } from "react";
import { InitialNameAvatar } from "../../../../components/images/initial-name-avatar";
import { ENUM_CURRENCY, RENDERER_TYPES } from "../../../../enums";
import {
  convertToFullName,
  getConvertedExternalUrl,
  numberFormat,
} from "../../../../services/utils";
import { useStyles } from "./styles";
import { LINKEDIN_PROFILE_ENTRIES } from "./client-details-data";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import EmailIcon from "@mui/icons-material/Email";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PeopleIcon from "@mui/icons-material/People";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CategoryIcon from "@mui/icons-material/Category";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import { Link } from "react-router-dom";
import { styled } from "@mui/material/styles";
import CopytoclipboardIcon from "../../../../components/CopytoclipboardIcon";

const TargetInfoCard = styled(Card)(({ theme }) => ({
  borderRadius: "8px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
  },
}));

function ClientInfoDetails({ data, fieldList }) {
  const classes = useStyles();
  const [isCompanyValuePropExpanded, setIsCompanyValuePropExpanded] =
    useState(false);

  const getFieldIcon = (field) => {
    const iconMap = {
      email: <EmailIcon />,
      secondaryMembers: <EmailIcon />,
      linkedIn: <LinkedInIcon />,
      title: <WorkIcon />,
      company: <BusinessIcon />,
      timezone: <ScheduleIcon />,
      no_of_emp: <PeopleIcon />,
      funding: <AttachMoneyIcon />,
      revenues: <TrendingUpIcon />,
      target_no_of_emp: <PeopleIcon />,
      target_account: <AccountBalanceIcon />,
      target_industries: <CategoryIcon />,
      budget: <AttachMoneyIcon />,
      licenseForMeeting: <MeetingRoomIcon />,
      viewAmbassadorThreshold: <PeopleIcon />,
      meetingThreshold: <MeetingRoomIcon />,
      viewNextBatchAmbassadorThreshold: <PeopleIcon />,
    };
    return iconMap[field] || <WorkIcon />;
  };

  const renderValue = (detail) => {
    const fieldValue = data[detail.field];

    // Check if value is empty, null, or undefined
    const isEmpty =
      !fieldValue ||
      (Array.isArray(fieldValue) && fieldValue.length === 0) ||
      (typeof fieldValue === "string" && fieldValue.trim() === "");

    if (isEmpty) {
      return <Typography className={classes.emptyValue}>-</Typography>;
    }

    if (detail.field === "email" && !isEmpty) {
      return (
        <Typography
          component="span"
          sx={{
            fontSize: "0.875rem",
            color: "text.secondary",
            wordBreak: "break-word",
          }}
        >
          {fieldValue}
          <CopytoclipboardIcon
            item={fieldValue}
            sx={{ p: 0, ml: 1.5, verticalAlign: "middle", display: "inline-flex" }}
            color="#070639"
            iconSize={16}
          />
        </Typography>
      );
    }

    switch (detail.renderer) {
      case RENDERER_TYPES.AMOUNT:
        return `${numberFormat(data[detail.field], ENUM_CURRENCY.USD)}`;
      case RENDERER_TYPES.TIMEZONE:
        return data.timezone?.value ? (
          `${data.timezone.value}`
        ) : (
          <Typography className={classes.emptyValue}>-</Typography>
        );
      case RENDERER_TYPES.NUMBER:
        return `${numberFormat(data[detail.field])}`;
      case RENDERER_TYPES.LINKEDIN_URL_WITH_ICON:
        return (
          <Link
            to={{ pathname: getConvertedExternalUrl(data[detail.field]) }}
            target="_blank"
            title="Linkedin URL"
            className={classes.linkedinLink}
          >
            <Box className={classes.cardValue}>View Profile</Box>
            <OpenInNewIcon className={classes.linkedinIcon} fontSize="small" />
          </Link>
        );
      case RENDERER_TYPES.MULTIPLE_TEXT_RENDERER:
        const multipleValues = data?.[detail?.field];
        if (!multipleValues || multipleValues.length === 0) {
          return <Typography className={classes.emptyValue}>-</Typography>;
        }
        return multipleValues.join(", ");
      case RENDERER_TYPES.CHIP_LIST:
        const chipValues = data[detail.field];
        if (!chipValues) {
          return <Typography className={classes.emptyValue}>-</Typography>;
        }

        // Handle if value is already an array
        let items = [];
        if (Array.isArray(chipValues)) {
          items = chipValues.filter((item) => item && item.trim() !== "");
        } else if (typeof chipValues === "string") {
          items = chipValues
            .split(",")
            .filter((item) => item && item.trim() !== "");
        }

        if (items.length === 0) {
          return <Typography className={classes.emptyValue}>-</Typography>;
        }

        return items.map((item) => (
          <Chip
            key={item}
            label={item.trim()}
            size="small"
            color="primary"
            variant="outlined"
            className={classes.chip}
          />
        ));
      default:
        return data[detail.field];
    }
  };

  // Check if field has multiple values that need special handling
  const isMultiValueField = (field) => {
    return [
      "target_no_of_emp",
      "target_account",
      "target_industries",
      "target_titles",
    ].includes(field);
  };

  const InfoCard = ({ detail, value }) => (
    <Card className={classes.infoCard}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardHeader}>
          <Box className={classes.cardIcon}>{getFieldIcon(detail.field)}</Box>
          <Typography variant="subtitle2" className={classes.cardLabel}>
            {detail.label}
          </Typography>
        </Box>
        <Box className={classes.cardValue}>{value}</Box>
      </CardContent>
    </Card>
  );

  const TargetInfoItem = ({ detail, value }) => (
    <Box className={classes.targetInfoItem}>
      <Typography variant="subtitle2" className={classes.targetInfoLabel}>
        {detail.label}
      </Typography>
      <Box className={classes.targetInfoValue}>{value}</Box>
    </Box>
  );

  // LinkedIn profiles: only entries that have a URL; always show role label + optional name
  const linkedInProfiles = LINKEDIN_PROFILE_ENTRIES.filter((entry) => {
    const url = data[entry.urlField];
    return url && typeof url === "string" && url.trim() !== "";
  });

  const companyValuePropText = useMemo(() => {
    const rawCompanyValueProp = data?.other_info;
    if (!rawCompanyValueProp) return "";
    return String(rawCompanyValueProp);
  }, [data?.other_info]);

  const COMPANY_VALUE_PROP_PREVIEW_LIMIT = 320;
  const hasCompanyValuePropOverflow =
    companyValuePropText.length > COMPANY_VALUE_PROP_PREVIEW_LIMIT;
  const companyValuePropPreviewText = hasCompanyValuePropOverflow
    ? `${companyValuePropText
        .slice(0, COMPANY_VALUE_PROP_PREVIEW_LIMIT)
        .trimEnd()}…`
    : companyValuePropText;

  return (
    <>
      {/* Profile Header */}
      <Box className={classes.profileHeader}>
        <InitialNameAvatar
          style={styles.profileAvatarContainer}
          textStyle={styles.profileAvatarText}
          name={data.firstName}
          text={convertToFullName(data.firstName, data.lastName)}
          image={Boolean(data.logo)}
          src={data.logo}
        />
      </Box>

      {/* LinkedIn Profiles – Grid: desktop same line, label and value on new lines */}
      {linkedInProfiles.length > 0 && (
        <Box className={classes.linkedInProfiles}>
          <Typography variant="overline" fontWeight={600} className={classes.linkedInProfilesTitle}>
            LinkedIn profiles
          </Typography>
          <Grid container spacing={1.5}>
            {linkedInProfiles.map((entry) => {
              const url = data[entry.urlField];
              const name = entry.nameField ? data[entry.nameField] : null;
              const hasName = name && typeof name === "string" && name.trim() !== "";
              return (
                <Grid item xs={12} md={4} key={entry.urlField}>
                  <Link
                    to={{ pathname: getConvertedExternalUrl(url) }}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={`View ${entry.label} profile`}
                    className={classes.linkedInProfilePillLink}
                  >
                    <LinkedInIcon className={classes.linkedInProfilePillIcon} />
                    <Box className={classes.linkedInProfilePillContent}>
                      <span className={classes.linkedInProfileRole}>{entry.label}</span>
                      <span className={classes.linkedInProfileName}>
                        {hasName ? name.trim() : "View profile"}
                      </span>
                    </Box>
                    <OpenInNewIcon className={classes.linkedInProfilePillArrow} fontSize="small" />
                  </Link>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      )}

      {companyValuePropText && (
        <Box className={classes.companyValueProps}>
          <Typography
            variant="overline"
            fontWeight={600}
            className={classes.companyValuePropsTitle}
          >
            Company Value Proposition
          </Typography>
          <Box className={classes.companyValuePropsBox}>
            {isCompanyValuePropExpanded
              ? companyValuePropText
              : companyValuePropPreviewText}
            {hasCompanyValuePropOverflow && (
              <Box display="flex" justifyContent="flex-end" mt={1}>
                <Button
                  size="small"
                  variant="text"
                  onClick={() =>
                    setIsCompanyValuePropExpanded((prev) => !prev)
                  }
                  sx={{ textTransform: "none", px: 0, color: "#6B7280" }}
                >
                  {isCompanyValuePropExpanded ? "Show less" : "Show more"}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Main Content Grid */}
      <Grid container spacing={2}>
        {/* Left Column - Regular Information Cards */}
        <Grid item xs={12} lg={8}>
          <Grid container spacing={2}>
            {fieldList
              .filter((detail) => !isMultiValueField(detail.field))
              .map((detail, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <InfoCard detail={detail} value={renderValue(detail)} />
                </Grid>
              ))}
          </Grid>
        </Grid>

        {/* Right Column - Target Information */}
        {fieldList.filter((detail) => isMultiValueField(detail.field)).length >
          0 && (
          <Grid item xs={12} lg={4}>
            <TargetInfoCard>
              <CardContent sx={{ p: 0 }}>
                <Box
                  sx={{
                    p: 1,
                    backgroundColor: "#F8FAFC",
                    borderBottom: "1px solid #E5E7EB",
                    borderRadius: "8px 8px 0 0",
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      color: "#374151",
                      margin: 0,
                    }}
                  >
                    Target Information
                  </Typography>
                </Box>
                <Box className={classes.targetInfoSidebar}>
                  {fieldList
                    .filter((detail) => isMultiValueField(detail.field))
                    .map((detail, index) => (
                      <TargetInfoItem
                        key={index}
                        detail={detail}
                        value={renderValue(detail)}
                      />
                    ))}
                </Box>
              </CardContent>
            </TargetInfoCard>
          </Grid>
        )}
      </Grid>
    </>
  );
}

export { ClientInfoDetails };

const styles = {
  profileAvatarContainer: {
    backgroundColor: "#070639",
    width: 60,
    height: 60,
    fontSize: 32,
  },
  profileAvatarText: {
    fontSize: 20,
  },
};
